<script lang="ts" setup>
import { useI18n } from "vue-i18n";
import {
  CheckCallMarker,
  EndPointMarker,
  SelectedPointMarker,
  StartDispatchPointMarker,
  StartPointMarker,
} from "@/constants/markers";

const { t } = useI18n();

type LegendMarker = google.maps.Symbol & { path: string; fillColor: string; strokeColor: string };

const startDispatchMarker = StartDispatchPointMarker as LegendMarker;
const pickUpMarker = StartPointMarker as LegendMarker;
const deliveryMarker = EndPointMarker as LegendMarker;
const currentCheckCallMarker = SelectedPointMarker as LegendMarker;
const previousCheckCallMarker = CheckCallMarker as LegendMarker;
</script>

<template>
  <div class="check-call-map-legend">
    <div class="check-call-map-legend__column">
      <div class="d-flex align-center check-call-map-legend__row">
        <div class="check-call-map-legend__icon">
          <div class="check-call-map-legend__path"></div>
        </div>
        <span class="font-weight-500 ml-3 text-grey-darken-2">{{ t("maps.planned-truck-path") }}</span>
      </div>

      <div class="d-flex align-center mt-5 check-call-map-legend__row">
        <div class="check-call-map-legend__icon">
          <div class="check-call-map-legend__path"></div>
        </div>
        <span class="font-weight-500 ml-3 text-grey-darken-2">{{ t("maps.real-truck-path") }}</span>
      </div>

      <div class="d-flex align-center mt-5 check-call-map-legend__row">
        <div class="check-call-map-legend__icon">
          <div class="check-call-map-legend__previous-path"></div>
        </div>

        <span class="font-weight-500 ml-3 text-grey-darken-2 check-call-map-legend__multiline">{{
          t("maps.planned-truck-path-from-previous-check-call")
        }}</span>
      </div>

      <div class="d-flex align-center mt-5 check-call-map-legend__row">
        <div class="check-call-map-legend__icon">
          <svg fill="none" height="22" viewBox="-2 -2 28 28" width="22">
            <path
              :d="previousCheckCallMarker.path"
              :fill="previousCheckCallMarker.fillColor"
              stroke="#304FFE"
              stroke-width="4"
            />
          </svg>
        </div>
        <span class="font-weight-500 ml-3 text-grey-darken-2">{{ t("maps.previous-check-call-location") }}</span>
      </div>
    </div>

    <div class="check-call-map-legend__column">
      <div class="d-flex align-center check-call-map-legend__row">
        <div class="check-call-map-legend__icon">
          <svg height="30" viewBox="0 0 18 24" width="24">
            <path
              :d="startDispatchMarker.path"
              :fill="startDispatchMarker.fillColor"
              :stroke="startDispatchMarker.strokeColor"
              stroke-width="1"
            />
          </svg>
        </div>
        <span class="font-weight-500 ml-3 text-grey-darken-2">{{ t("maps.start-point") }}</span>
      </div>

      <div class="d-flex align-center mt-5 check-call-map-legend__row">
        <div class="check-call-map-legend__icon">
          <svg height="30" viewBox="0 0 18 24" width="24">
            <path
              :d="pickUpMarker.path"
              :fill="pickUpMarker.fillColor"
              :stroke="pickUpMarker.strokeColor"
              stroke-width="1"
            />
          </svg>
        </div>
        <span class="font-weight-500 ml-3 text-grey-darken-2">{{ t("maps.pick-up-point") }}</span>
      </div>

      <div class="d-flex align-center mt-5 check-call-map-legend__row">
        <div class="check-call-map-legend__icon">
          <svg height="30" viewBox="0 0 18 24" width="24">
            <path
              :d="deliveryMarker.path"
              :fill="deliveryMarker.fillColor"
              :stroke="deliveryMarker.strokeColor"
              stroke-width="1"
            />
          </svg>
        </div>
        <span class="font-weight-500 ml-3 text-grey-darken-2">{{ t("maps.delivery-point") }}</span>
      </div>

      <div class="d-flex align-center mt-5 check-call-map-legend__row">
        <div class="check-call-map-legend__icon">
          <svg height="30" viewBox="0 0 18 24" width="24">
            <path
              :d="currentCheckCallMarker.path"
              :fill="currentCheckCallMarker.fillColor"
              :stroke="currentCheckCallMarker.strokeColor"
              stroke-width="1"
            />
          </svg>
        </div>
        <span class="font-weight-500 ml-3 text-grey-darken-2">{{ t("maps.current-check-call-point") }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.check-call-map-legend {
  display: flex;
  gap: 12px;
  margin-top: 20px;

  &__column {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 250px;
  }

  &__icon {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 26px;
  }

  &__path {
    width: 20px;
    height: 9px;
    background-color: #304ffe;
  }

  &__previous-path {
    display: flex;
    gap: 4px;

    &::before,
    &::after {
      content: "";
      width: 10px;
      height: 9px;
      background-color: #304ffe;
    }
  }

  &__multiline {
    white-space: pre-line;
  }
}
</style>
